import { useCallback } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { BreakRuleBase } from 'repository/timekeeper/@types/BreakRuleModel';
import { SnackbarMessage } from 'notistack';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const useRestParametersActions = () => {
    const addBreakRule = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: BreakRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);
            //console.log('Adding Break Rule with body:', body);

            try {
                await timekeeperService.break_rule.createBreakRule(body);
                handlerAlert(messageSuccess);
                closing();
                reloading();
            } catch (error) {
                console.error('Error adding break rule:', error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateBreakRule = useCallback(
        async ({
            setLoading,
            breakRuleId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            breakRuleId: number;
            body: BreakRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);
            //console.log('Updating Break Rule with ID:', breakRuleId, 'and body:', body);

            try {
                await timekeeperService.break_rule.updateBreakRule(breakRuleId, body);
                handlerAlert(messageSuccess);
                closing();
                reloading();
            } catch (error) {
                console.error('Error updating break rule:', error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteBreakRule = useCallback(
        async ({
            setLoading,
            breakRuleId,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            breakRuleId: number;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);
            //console.log('Deleting Break Rule with ID:', breakRuleId);

            try {
                await timekeeperService.break_rule.deleteBreakRules(breakRuleId);
                handlerAlert(messageSuccess);
                closing();
                reloading();
            } catch (error) {
                console.error('Error deleting break rule:', error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );
    return { addBreakRule, updateBreakRule, deleteBreakRule };
};
